/**
 * Country validation utilities for IP checking and country restrictions
 */

// Numeric country IDs (for user.country_id / user.country?.id)
export const COUNTRY_IDS = { NIGERIA: 161, USA: 233, INDIA: 251 } as const

export const isNigeria = (id: number) => id === COUNTRY_IDS.NIGERIA
export const isIndia = (id: number) => id === COUNTRY_IDS.INDIA
export const usesNGN = (id: number) => id === COUNTRY_IDS.NIGERIA
export const usesUSD = (id: number) =>
  id === COUNTRY_IDS.USA || id === COUNTRY_IDS.INDIA

// Supported countries (Nigeria: 161, USA: 233, India: 251)
const SUPPORTED_COUNTRIES = {
  NIGERIA: '161',
  USA: '233',
  India: '251'
}

// Default country when detection fails (USA)
export const DEFAULT_COUNTRY_ID = '233'

// Country codes mapping
const COUNTRY_CODES = {
  '161': 'Nigeria',
  '233': 'United States of America',
  '251': 'India'
}

// ISO code to API country ID mapping
const IP_COUNTRY_MAPPING: Record<string, string> = {
  NG: '161', // Nigeria
  US: '233', // USA
  IN: '251' // India
}

const DETECTED_COUNTRY_CACHE_KEY = 'detected_country'
const CACHE_TTL_MS = 24 * 60 * 60 * 1000 // 24 hours

/**
 * ISO country code → language code for auto-language when no user preference.
 * Covers all languages in the language switcher.
 */
export const ISO_COUNTRY_TO_LANGUAGE: Record<string, string> = {
  // English
  US: 'en', GB: 'en', AU: 'en', CA: 'en', IE: 'en', NZ: 'en', NG: 'en',
  // Spanish
  ES: 'es', MX: 'es', AR: 'es', CO: 'es', CL: 'es', PE: 'es', VE: 'es', EC: 'es',
  // French
  FR: 'fr', BE: 'fr', SN: 'fr', CI: 'fr', CM: 'fr', ML: 'fr', NE: 'fr',
  // Chinese
  CN: 'zh', TW: 'zh', HK: 'zh', MO: 'zh',
  // Arabic
  SA: 'ar', AE: 'ar', EG: 'ar', JO: 'ar', KW: 'ar', QA: 'ar', BH: 'ar', OM: 'ar',
  // Portuguese
  PT: 'pt', BR: 'pt', AO: 'pt', MZ: 'pt',
  // Hindi (India)
  IN: 'hi',
  // Swahili
  TZ: 'sw', KE: 'sw', UG: 'sw',
}

/**
 * Get suggested language for ISO country code (for language switcher).
 */
export const getLanguageForCountry = (isoCode: string | null): string | null => {
  if (!isoCode) return null
  return ISO_COUNTRY_TO_LANGUAGE[isoCode.toUpperCase()] ?? null
}

/**
 * Get user's country from IP address (ipapi.co, fallback ipinfo.io)
 */
export const getUserCountryFromIP = async (): Promise<string | null> => {
  const tryIpInfo = async (): Promise<string | null> => {
    try {
      const res = await fetch('https://ipinfo.io/json')
      const data = await res.json()
      return data.country || null
    } catch {
      return null
    }
  }

  try {
    const response = await fetch('https://ipapi.co/json/')
    const data = await response.json()
    const code = data.country_code || null
    if (code) return code
    return tryIpInfo()
  } catch (error) {
    console.error('Error fetching country from IP:', error)
    return tryIpInfo()
  }
}

/**
 * Get detected country ID with caching.
 * Returns API country ID (161/233/251) or null if detection fails.
 */
export const getDetectedCountryId = async (): Promise<string | null> => {
  try {
    const cached = sessionStorage.getItem(DETECTED_COUNTRY_CACHE_KEY)
    if (cached) {
      const { countryId, ts } = JSON.parse(cached)
      if (Date.now() - ts < CACHE_TTL_MS) return countryId
    }

    const isoCode = await getUserCountryFromIP()
    if (!isoCode) return null

    const countryId = IP_COUNTRY_MAPPING[isoCode]
    if (!countryId) return null

    sessionStorage.setItem(
      DETECTED_COUNTRY_CACHE_KEY,
      JSON.stringify({ countryId, isoCode, ts: Date.now() })
    )
    return countryId
  } catch (error) {
    console.error('Error getting detected country:', error)
    return null
  }
}

/**
 * Check if country is supported
 */
export const isCountrySupported = (countryId: string): boolean => {
  return Object.values(SUPPORTED_COUNTRIES).includes(countryId)
}

/**
 * Get country name from ID
 */
export const getCountryName = (countryId: string): string => {
  return COUNTRY_CODES[countryId as keyof typeof COUNTRY_CODES] || 'Unknown'
}

/**
 * Get phone input country code from API country ID (for FormPhoneNumber defaultCountry)
 */
export const getPhoneCountryCode = (countryId: string): string => {
  const map: Record<string, string> = { '161': 'ng', '233': 'us', '251': 'in' }
  return map[countryId] || 'us'
}

/**
 * Validate country selection
 */
export const validateCountrySelection = (countryId: string): {
  isValid: boolean
  error?: string
  countryName?: string
} => {
  if (!countryId) {
    return { isValid: false, error: 'Please select a country' }
  }

  if (!isCountrySupported(countryId)) {
    return { 
      isValid: false, 
      error: 'We do not operate in the selected country' 
    }
  }

  return { 
    isValid: true, 
    countryName: getCountryName(countryId) 
  }
}

/**
 * Check if user's IP country matches selected country
 */
export const validateIPCountryMatch = async (
  selectedCountryId: string
): Promise<{
  isValid: boolean
  error?: string
  ipCountry?: string
  selectedCountry?: string
}> => {
  try {
    const ipCountry = await getUserCountryFromIP()
    
    if (!ipCountry) {
      // If we can't get IP country, allow but warn
      return { 
        isValid: true,
        error: 'Unable to verify location. Please ensure you are in the correct country.'
      }
    }

    const ipCountryId = IP_COUNTRY_MAPPING[ipCountry]
    
    if (ipCountryId && ipCountryId !== selectedCountryId) {
      return {
        isValid: false,
        error: `Your location (${ipCountry}) doesn't match the selected country (${getCountryName(selectedCountryId)}). Please select the correct country.`,
        ipCountry,
        selectedCountry: getCountryName(selectedCountryId)
      }
    }

    return { 
      isValid: true,
      ipCountry,
      selectedCountry: getCountryName(selectedCountryId)
    }
  } catch (error) {
    console.error('Error validating IP country:', error)
    return { 
      isValid: true,
      error: 'Unable to verify location. Please ensure you are in the correct country.'
    }
  }
}

/**
 * Get supported countries list
 */
export const getSupportedCountries = () => {
  return Object.entries(COUNTRY_CODES).map(([id, name]) => ({
    id,
    name
  }))
}
